import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Card, Typography, Empty, Alert, Skeleton } from 'antd';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { useHistory } from 'react-router-dom';
import { urgencyTypes, urgencyColors, statusTypes } from '../configs/task.config';
import '../styles/KanbanColumnScrollbar.css';
import '../styles/KanbanStyles.css';

const { Title } = Typography;

// Get all status columns from config, but prioritize main ones
const mainStatusOrder = [
  statusTypes['Not Started'],
  statusTypes['In Progress'],
  statusTypes['Completed'],
];
const allStatusKeys = Object.values(statusTypes);
const uniqueStatusKeys = [
  ...mainStatusOrder,
  ...allStatusKeys.filter((s) => !mainStatusOrder.includes(s)),
];
const statusColumns = uniqueStatusKeys.map((key) => {
  const label = Object.keys(statusTypes).find((k) => statusTypes[k] === key) || key;

  return { key, label };
});

// Helper to get urgency color from config
const getUrgencyColor = (urgency) => {
  if (!urgency) return urgencyColors[urgencyTypes.Low];
  // Try to match config value (case-insensitive)
  const match = Object.values(urgencyTypes).find((t) => t.toLowerCase() === urgency.toLowerCase());
  return urgencyColors[match] || urgencyColors[urgencyTypes.Low];
};

// Virtualized column component with expand/collapse functionality
const VirtualizedColumnWithExpansion = ({
  col,
  tasks,
  expandedColumns,
  setExpandedColumns,
  getUrgencyColor,
  setError,
  history,
}) => {
  const maxInitialTasks = 30;
  const maxExpandedTasks = 100; // Show up to 100 when expanded
  const isExpanded = expandedColumns[col.key];
  const tasksToShow = isExpanded ? maxExpandedTasks : maxInitialTasks;
  const visibleTasks = tasks.slice(0, tasksToShow);
  const remainingTasks = tasks.length - visibleTasks.length;

  return (
    <div
      role="list"
      aria-label={`Tasks in ${col.label} column (showing ${visibleTasks.length} of ${tasks.length})`}
    >
      {visibleTasks
        .map((task) => {
          // Validate task data
          if (!task || !task.id) {
            console.warn('Invalid task data:', task);
            return null;
          }

          return (
            <Card
              key={task.id}
              size="small"
              hoverable
              role="listitem"
              aria-label={`Task: ${task.name || 'Untitled Task'}. Due: ${
                task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date'
              }. Urgency: ${task.urgency || 'Low'}. Click to view details.`}
              aria-describedby={`task-${task.id}-details`}
              className="kanban-task-card"
              style={{
                borderLeft: `4px solid ${getUrgencyColor(task.urgency)}`,
              }}
              onClick={() => {
                try {
                  if (!task.id) {
                    setError('Invalid task ID. Cannot navigate to details.');
                    return;
                  }
                  history.push(`/tasks/details/${task.id}`);
                } catch (err) {
                  console.error('Navigation error:', err);
                  setError('Failed to navigate to task details.');
                }
              }}
            >
              <div id={`task-${task.id}-details`} style={{ display: 'none' }}>
                Task details: {task.name || 'Untitled Task'}. Status: {col.label}. Due date:{' '}
                {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date'}.
                Urgency level: {task.urgency || 'Low'}.
              </div>

              <div
                className="kanban-task-name"
                title={task.name || 'Untitled Task'}
                aria-hidden="true"
              >
                {task.name || 'Untitled Task'}
              </div>
              <div className="kanban-task-due-date" aria-hidden="true">
                Due: {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'N/A'}
              </div>
            </Card>
          );
        })
        .filter(Boolean)}

      {/* Show More / Show Less buttons */}
      {remainingTasks > 0 && (
        <div
          style={{
            padding: 12,
            textAlign: 'center',
            background: '#f8fafc',
            borderRadius: 4,
            marginTop: 8,
            border: '1px dashed #d1d5db',
          }}
        >
          <button
            onClick={() => {
              const newExpandedColumns = {
                ...expandedColumns,
                [col.key]: !expandedColumns[col.key],
              };
              setExpandedColumns(newExpandedColumns);
              // Persist to localStorage
              try {
                localStorage.setItem('kanban-expanded-columns', JSON.stringify(newExpandedColumns));
              } catch (err) {
                console.warn('Failed to save expanded columns state:', err);
              }
            }}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.target.style.background = '#2563eb')}
            onMouseLeave={(e) => (e.target.style.background = '#3b82f6')}
          >
            {isExpanded
              ? `Show Less (hide ${visibleTasks.length - maxInitialTasks} tasks)`
              : `Show More (+${remainingTasks} tasks)`}
          </button>
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              color: '#666',
            }}
          >
            Showing {visibleTasks.length} of {tasks.length} tasks
          </div>
        </div>
      )}

      {/* Show "All tasks visible" when fully expanded */}
      {remainingTasks === 0 && tasks.length > maxInitialTasks && (
        <div
          style={{
            padding: 12,
            textAlign: 'center',
            background: '#f0fdf4',
            borderRadius: 4,
            marginTop: 8,
            fontSize: 14,
            color: '#15803d',
            border: '1px solid #bbf7d0',
          }}
        >
          <span role="img" aria-label="checkmark">
            ✓
          </span>{' '}
          All {tasks.length} tasks are now visible
          <button
            onClick={() => {
              const newExpandedColumns = {
                ...expandedColumns,
                [col.key]: false,
              };
              setExpandedColumns(newExpandedColumns);
              // Persist to localStorage
              try {
                localStorage.setItem('kanban-expanded-columns', JSON.stringify(newExpandedColumns));
              } catch (err) {
                console.warn('Failed to save expanded columns state:', err);
              }
            }}
            style={{
              background: 'transparent',
              color: '#15803d',
              border: '1px solid #15803d',
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: 12,
              cursor: 'pointer',
              marginLeft: 8,
            }}
          >
            Collapse
          </button>
        </div>
      )}
    </div>
  );
};

const Kanban = ({ tasks }) => {
  const { task: contextTask, tasksLoading } = useTaskContext();
  const history = useHistory();
  const task = Array.isArray(tasks) ? tasks : contextTask;

  // Local error state
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const kanbanRef = useRef(null);

  // Virtualization state
  const [enableVirtualization, setEnableVirtualization] = useState(false);
  const [expandedColumns, setExpandedColumns] = useState(() => {
    // Initialize from localStorage to persist expanded state across navigation
    try {
      const stored = localStorage.getItem('kanban-expanded-columns');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Error boundary effect
  useEffect(() => {
    if (task && Array.isArray(task) && task.length === 0 && !tasksLoading) {
      // Reset error when we have valid empty data
      setError(null);
    }
  }, [task, tasksLoading]);

  // Virtualization detection - enable for large datasets
  useEffect(() => {
    const totalTasks = task ? task.length : 0;
    const shouldVirtualize = totalTasks > 50; // Re-enable virtualization with fixed implementation
    setEnableVirtualization(shouldVirtualize);

    // Debug log
    console.log(`Total tasks: ${totalTasks}, Virtualization: ${shouldVirtualize}`);
  }, [task]);

  // Group tasks by status (case-insensitive match) and sort by due date in Kanban
  const grouped = useMemo(() => {
    if (!Array.isArray(task)) {
      return {};
    }

    try {
      const filteredTasks = (colKey) => {
        return task.filter((t) => {
          // Ensure task has required properties
          if (!t || typeof t !== 'object') return false;
          return (t.status || '').toUpperCase() === colKey.toUpperCase();
        });
      };

      return statusColumns.reduce((acc, col) => {
        try {
          // Sort tasks in each column by due date ascending (soonest first)
          const sortedTasks = filteredTasks(col.key)
            .slice()
            .sort((a, b) => {
              try {
                const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
                const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;

                // Handle invalid dates
                if (isNaN(aDate) && isNaN(bDate)) return 0;
                if (isNaN(aDate)) return 1;
                if (isNaN(bDate)) return -1;

                return aDate - bDate;
              } catch (sortErr) {
                console.warn('Error sorting tasks:', sortErr);
                return 0;
              }
            });
          acc[col.key] = sortedTasks;
        } catch (columnErr) {
          console.warn(`Error processing column ${col.key}:`, columnErr);
          acc[col.key] = [];
        }
        return acc;
      }, {});
    } catch (err) {
      console.error('Error grouping tasks:', err);
      setError('Failed to process task data. Please refresh the page.');
      return {};
    }
  }, [task]);

  // Note: Kanban state persistence is now handled by TaskBoard.js using user-specific storage

  // Handle retry functionality
  const handleRetry = async () => {
    setIsRetrying(true);
    setError(null);
    try {
      // If there's a refetch method available, use it
      // Otherwise, just reset the error state
      setTimeout(() => {
        setIsRetrying(false);
      }, 1000);
    } catch (err) {
      setError('Failed to retry loading tasks. Please try again.');
      setIsRetrying(false);
    }
  };

  // Show error state
  if (error) {
    return (
      <div className="kanban-error-container">
        <Alert
          message="Error Loading Tasks"
          description={error}
          type="error"
          showIcon
          action={
            <button onClick={handleRetry} disabled={isRetrying} className="kanban-retry-button">
              {isRetrying ? 'Retrying...' : 'Retry'}
            </button>
          }
        />
      </div>
    );
  }

  // Show loading spinner if tasks are being fetched
  if (tasksLoading || isRetrying) {
    return (
      <div className="kanban-container">
        <div className="kanban-total-tasks">
          <Skeleton.Input style={{ width: 150 }} active />
        </div>
        <div className="kanban-row">
          {statusColumns.map((_, index) => (
            <div key={index} className="kanban-column">
              <Card
                title={<Skeleton.Input style={{ width: 100 }} active />}
                className="kanban-column-card"
                bodyStyle={{ padding: 12, flex: 1, overflowY: 'auto' }}
              >
                {[...Array(3)].map((_, cardIndex) => (
                  <div key={cardIndex} className="kanban-skeleton-card">
                    <Skeleton active paragraph={{ rows: 2 }} />
                  </div>
                ))}
              </Card>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Defensive: if no tasks, show empty
  if (!Array.isArray(task)) {
    const errorMessage = 'Unable to load tasks. Please refresh the page or contact support.';
    setError(errorMessage);
    return <Empty description="No tasks" />;
  }

  // Calculate total tasks (all tasks)
  const kanbanTotal = task.length;

  return (
    <div
      ref={kanbanRef}
      role="application"
      aria-label="Task management kanban board"
      className="kanban-container"
    >
      {/* Accessibility instructions - hidden but readable by screen readers */}
      <div
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        aria-live="polite"
      >
        Kanban board with {kanbanTotal} tasks organized in {statusColumns.length} columns.
        {enableVirtualization && 'Performance mode enabled for large task list. '}
        Click on any task to view details.
      </div>

      <h2 className="kanban-total-tasks" aria-live="polite" aria-atomic="true">
        Total Tasks: {kanbanTotal}
        {enableVirtualization && (
          <span
            style={{
              fontSize: 12,
              color: '#52c41a',
              marginLeft: 8,
              fontWeight: 400,
            }}
            title="Performance mode enabled for large task lists"
          >
            <span role="img" aria-label="lightning bolt">
              ⚡
            </span>{' '}
            Optimized
          </span>
        )}
      </h2>
      <div role="grid" aria-label="Task columns" className="kanban-row">
        {statusColumns &&
          statusColumns.map((col, columnIndex) => (
            <div
              key={col.key}
              role="gridcell"
              aria-label={`${col.label} column with ${
                grouped[col.key] ? grouped[col.key].length : 0
              } tasks`}
              className="kanban-column"
            >
              <Card
                title={
                  <div className="kanban-column-header">
                    <Title
                      level={4}
                      className="kanban-column-title"
                      id={`column-${columnIndex}-title`}
                    >
                      {col.label}
                    </Title>
                    <span
                      className="kanban-column-badge"
                      aria-label={`${
                        grouped && Array.isArray(grouped[col.key]) && grouped[col.key].length
                          ? grouped[col.key].length
                          : 0
                      } tasks`}
                    >
                      {grouped && Array.isArray(grouped[col.key]) && grouped[col.key].length
                        ? grouped[col.key].length
                        : 0}
                    </span>
                  </div>
                }
                bordered={false}
                className="kanban-column-card"
                bodyStyle={{
                  padding: 12,
                  flex: 1,
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
                role="region"
                aria-labelledby={`column-${columnIndex}-title`}
              >
                {enableVirtualization && grouped[col.key] && grouped[col.key].length > 50 ? (
                  <VirtualizedColumnWithExpansion
                    col={col}
                    tasks={grouped[col.key]}
                    expandedColumns={expandedColumns}
                    setExpandedColumns={setExpandedColumns}
                    getUrgencyColor={getUrgencyColor}
                    setError={setError}
                    history={history}
                  />
                ) : (
                  <div role="list" aria-label={`Tasks in ${col.label} column`}>
                    {grouped[col.key] && grouped[col.key].length > 0 ? (
                      grouped[col.key]
                        .map((task) => {
                          // Validate task data
                          if (!task || !task.id) {
                            console.warn('Invalid task data:', task);
                            return null;
                          }

                          return (
                            <Card
                              key={task.id}
                              size="small"
                              hoverable
                              role="listitem"
                              aria-label={`Task: ${task.name || 'Untitled Task'}. Due: ${
                                task.dueDate
                                  ? new Date(task.dueDate).toLocaleDateString()
                                  : 'No due date'
                              }. Urgency: ${task.urgency || 'Low'}. Click to view details.`}
                              aria-describedby={`task-${task.id}-details`}
                              className="kanban-task-card"
                              style={{
                                borderLeft: `4px solid ${getUrgencyColor(task.urgency)}`,
                              }}
                              onClick={() => {
                                try {
                                  if (!task.id) {
                                    setError('Invalid task ID. Cannot navigate to details.');
                                    return;
                                  }
                                  history.push(`/tasks/details/${task.id}`);
                                } catch (err) {
                                  console.error('Navigation error:', err);
                                  setError('Failed to navigate to task details.');
                                }
                              }}
                            >
                              <div id={`task-${task.id}-details`} style={{ display: 'none' }}>
                                Task details: {task.name || 'Untitled Task'}. Status: {col.label}.
                                Due date:{' '}
                                {task.dueDate
                                  ? new Date(task.dueDate).toLocaleDateString()
                                  : 'No due date'}
                                . Urgency level: {task.urgency || 'Low'}.
                              </div>

                              <div
                                className="kanban-task-name"
                                title={task.name || 'Untitled Task'}
                                aria-hidden="true"
                              >
                                {task.name || 'Untitled Task'}
                              </div>
                              <div className="kanban-task-due-date" aria-hidden="true">
                                Due:{' '}
                                {(() => {
                                  try {
                                    if (!task.dueDate) return 'N/A';
                                    const date = new Date(task.dueDate);
                                    return isNaN(date.getTime())
                                      ? 'Invalid Date'
                                      : date.toLocaleDateString();
                                  } catch (err) {
                                    console.warn('Error formatting date:', err);
                                    return 'Invalid Date';
                                  }
                                })()}
                              </div>
                            </Card>
                          );
                        })
                        .filter(Boolean) // Remove null entries
                    ) : (
                      <Empty
                        description="No tasks"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        className="kanban-empty-state"
                        role="status"
                        aria-label={`No tasks in ${col.label} column`}
                      />
                    )}
                  </div>
                )}
              </Card>
            </div>
          ))}
      </div>
    </div>
  );
};

export default Kanban;
